"use client";

import clsx from "clsx";
import { motion, useReducedMotion } from "framer-motion";

const WRITE_EASE = [0.22, 1, 0.36, 1] as const;
const START_DELAY_S = 0.55;
const CHAR_DELAY_S = 0.058;
const CHAR_DURATION_S = 0.52;

type WordToken = {
  text: string;
  startIndex: number;
};

function tokenizeLine(line: string): WordToken[] {
  const tokens: WordToken[] = [];
  const re = /\S+/g;
  let match: RegExpExecArray | null;
  while ((match = re.exec(line)) !== null) {
    tokens.push({ text: match[0], startIndex: match.index });
  }
  return tokens;
}

const greetingClassName =
  "font-caveat text-5xl font-medium leading-tight tracking-wide text-white sm:text-6xl md:text-7xl";

export type AnimatedWelcomeGreetingProps = {
  greeting: string;
  personaName: string;
  className?: string;
};

/** macOS-style welcome line — large cursive text that reveals character by character. */
export function AnimatedWelcomeGreeting({
  greeting,
  personaName,
  className,
}: AnimatedWelcomeGreetingProps) {
  const reduceMotion = useReducedMotion();
  const trimmedName = personaName.trim();
  const line = trimmedName ? `${greeting}, ${trimmedName}.` : `${greeting}.`;
  const nameStartIndex = trimmedName ? `${greeting}, `.length : line.length;
  const wordTokens = tokenizeLine(line);

  if (reduceMotion) {
    return (
      <p className={clsx(greetingClassName, className)}>
        {wordTokens.map((token, tokenIndex) => (
          <span key={token.startIndex}>
            {tokenIndex > 0 ? " " : null}
            <span
              className={clsx(
                "whitespace-nowrap",
                token.startIndex >= nameStartIndex && "text-indigo-200",
              )}
            >
              {token.text}
            </span>
          </span>
        ))}
      </p>
    );
  }

  return (
    <p
      className={clsx(greetingClassName, className)}
      aria-label={line}
      data-testid="workspace-welcome-greeting"
    >
      {wordTokens.map((token, tokenIndex) => (
        <span key={token.startIndex}>
          {tokenIndex > 0 ? " " : null}
          <span className="inline-block whitespace-nowrap">
            {Array.from(token.text).map((char, charIndex) => {
              const index = token.startIndex + charIndex;
              return (
                <motion.span
                  key={`${index}-${char}`}
                  className={clsx(
                    "inline-block",
                    index >= nameStartIndex && "text-indigo-200",
                  )}
                  initial={{ opacity: 0, y: 12, filter: "blur(10px)" }}
                  animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                  transition={{
                    delay: START_DELAY_S + index * CHAR_DELAY_S,
                    duration: CHAR_DURATION_S,
                    ease: WRITE_EASE,
                  }}
                >
                  {char}
                </motion.span>
              );
            })}
          </span>
        </span>
      ))}
    </p>
  );
}

/** When the greeting write-on finishes — use to stagger follow-up copy. */
export function welcomeGreetingRevealDurationMs(
  charCount: number,
  startDelayS = START_DELAY_S,
  charDelayS = CHAR_DELAY_S,
  charDurationS = CHAR_DURATION_S,
): number {
  if (charCount <= 0) return startDelayS * 1000;
  const lastCharStart = startDelayS + (charCount - 1) * charDelayS;
  return (lastCharStart + charDurationS) * 1000;
}
